import React, { useState, useEffect, useRef } from "react";
import { 
    Modal, 
    Box, 
    IconButton, 
    Fade, 
    Typography, 
    Button,
    Backdrop,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {
    Close as CloseIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    ChevronLeft,
    ChevronRight,
    FullscreenExit,
    Fullscreen,
    RotateLeft,
    RotateRight
} from "@mui/icons-material";
import "./ContractPreviewModal.module.css";

const ContractPreviewModal = ({ 
    open, 
    onClose, 
    pages, 
    currentPage: initialPage = 0,
    signatures = {},
    profileData = {},
    Page3Image = null
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState({ x: 0, y: 0 });
    
    const containerRef = useRef(null);
    const canvasRef = useRef(null);

    // Reset zoom and position when page changes
    useEffect(() => {
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
        setRotation(0);
    }, [currentPage]);

    // Handle canvas drawing for page 3 when modal opens or page changes
    useEffect(() => {
        if (open && currentPage === 2 && canvasRef.current) {
            const drawPageThreeCanvas = () => {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext("2d");
                const img = new Image();
                
                img.onload = () => {
                    const aspectRatio = img.width / img.height;
                    canvas.width = img.width;
                    canvas.height = canvas.width / aspectRatio;
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    // Draw signature if available
                    if (signatures.signature && signatures.signMethod === 0) {
                        const signatureImg = new Image();
                        signatureImg.src = signatures.signature;
                        signatureImg.onload = () => {
                            const x = canvas.width / 6.5;
                            const y = canvas.height / 1.7;
                            ctx.drawImage(signatureImg, x, y);
                        };
                    } else if (signatures.typedSignature && signatures.signMethod === 1) {
                        const x = canvas.width / 6.5;
                        const y = canvas.height / 1.6;
                        ctx.font = "30px cursive";
                        ctx.fillStyle = signatures.signatureColor || "#000000";
                        ctx.fillText(signatures.typedSignature, x, y);
                    }

                    // Draw profile name if available
                    if (profileData?.first_name || profileData?.last_name) {
                        ctx.font = "bold 20px Arial";
                        ctx.fillStyle = "black";
                        const nameX = canvas.width * 0.1;
                        const nameY = canvas.height * 0.36;
                        ctx.fillText(`${profileData?.first_name || ''} ${profileData?.last_name || ''}`, nameX, nameY);
                    }

                    // Draw current date
                    ctx.font = "bold 30px Arial";
                    ctx.fillStyle = "red";
                    const dateX = canvas.width / 1.26;
                    const dateY = canvas.height / 1.62;
                    ctx.fillText(new Date().toLocaleDateString(), dateX, dateY);
                };
                
                // Use the Page3Image prop or find it from pages
                const page3Data = pages.find(p => p.type === 'canvas');
                if (Page3Image) {
                    img.src = Page3Image;
                } else if (page3Data?.src) {
                    img.src = page3Data.src;
                } else {
                    // Fallback - try to find an image with id containing 'page3'
                    const page3Fallback = pages.find(p => p.id && p.id.includes('page3'));
                    if (page3Fallback?.src) {
                        img.src = page3Fallback.src;
                    }
                }
            };

            // Small delay to ensure canvas is rendered
            setTimeout(drawPageThreeCanvas, 100);
        }
    }, [open, currentPage, signatures, profileData, Page3Image, pages]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!open) return;
            
            switch (e.key) {
                case 'ArrowLeft':
                    handlePreviousPage();
                    break;
                case 'ArrowRight':
                    handleNextPage();
                    break;
                case 'Escape':
                    onClose();
                    break;
                case '+':
                case '=':
                    handleZoomIn();
                    break;
                case '-':
                    handleZoomOut();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, currentPage, pages.length]);

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.25, 3));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => (prev + 1) % pages.length);
    };

    const handlePreviousPage = () => {
        setCurrentPage(prev => (prev - 1 + pages.length) % pages.length);
    };

    const handleResetZoom = () => {
        setZoomLevel(1);
        setPosition({ x: 0, y: 0 });
        setRotation(0);
    };

    const handleRotateLeft = () => {
        setRotation(prev => prev - 90);
    };

    const handleRotateRight = () => {
        setRotation(prev => prev + 90);
    };

    const toggleFullscreen = () => {
        setIsFullscreen(prev => !prev);
    };

    // Mouse drag functionality for panning when zoomed
    const handleMouseDown = (e) => {
        if (zoomLevel > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && zoomLevel > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Touch support for mobile
    const handleTouchStart = (e) => {
        if (zoomLevel > 1 && e.touches.length === 1) {
            setIsDragging(true);
            setDragStart({
                x: e.touches[0].clientX - position.x,
                y: e.touches[0].clientY - position.y
            });
        }
    };

    const handleTouchMove = (e) => {
        if (isDragging && zoomLevel > 1 && e.touches.length === 1) {
            e.preventDefault();
            setPosition({
                x: e.touches[0].clientX - dragStart.x,
                y: e.touches[0].clientY - dragStart.y
            });
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    // Render current page content
    const renderPageContent = () => {
        const currentPageData = pages[currentPage];
        
        if (currentPageData.type === 'canvas') {
            return (
                <canvas
                    ref={canvasRef}
                    id={currentPageData.id}
                    style={{
                        width: '100%',
                        height: 'auto',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel}) rotate(${rotation}deg)`,
                        transformOrigin: 'center center',
                        cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                        transition: isDragging ? 'none' : 'transform 0.2s ease'
                    }}
                />
            );
        }
        
        return (
            <img
                src={currentPageData.src}
                alt={`Contract Page ${currentPage + 1}`}
                style={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel}) rotate(${rotation}deg)`,
                    transformOrigin: 'center center',
                    cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    transition: isDragging ? 'none' : 'transform 0.2s ease'
                }}
                draggable={false}
            />
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
                sx: { 
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    backdropFilter: 'blur(4px)'
                }
            }}
            sx={{
                zIndex: 1300,
                '& .MuiModal-root': {
                    zIndex: 1300
                }
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        position: 'fixed',
                        top: isFullscreen ? 0 : { xs: '5%', sm: '5%', md: '5%' },
                        left: isFullscreen ? 0 : { xs: '5%', sm: '5%', md: '5%' },
                        width: isFullscreen ? '100vw' : { xs: '90vw', sm: '90vw', md: '90vw', lg: '85vw', xl: '80vw' },
                        height: isFullscreen ? '100vh' : { xs: '90vh', sm: '90vh', md: '90vh', lg: '90vh', xl: '90vh' },
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'background.paper',
                        outline: 'none',
                        overflow: 'hidden',
                        borderRadius: isFullscreen ? 0 : { xs: 1, sm: 2, md: 3 },
                        boxShadow: isFullscreen ? 'none' : { xs: 8, sm: 12, md: 24 },
                        zIndex: 1400,
                        maxWidth: isFullscreen ? 'none' : { xs: '100vw', sm: '100vw', md: '1400px' },
                        maxHeight: isFullscreen ? 'none' : { xs: '100vh', sm: '100vh', md: '95vh' }
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: { xs: 1, sm: 1.5, md: 2 },
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                            minHeight: { xs: 48, sm: 56, md: 64 },
                            flexShrink: 0
                        }}
                    >
                        <Typography 
                            variant={isMobile ? "subtitle1" : "h6"} 
                            sx={{ 
                                fontWeight: 600, 
                                color: 'text.primary',
                                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                                flexShrink: 1,
                                minWidth: 0
                            }}
                        >
                            {isMobile ? `Page ${currentPage + 1}/${pages.length}` : `Contract Preview - Page ${currentPage + 1} of ${pages.length}`}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                                onClick={toggleFullscreen}
                                size="small"
                                sx={{ 
                                    bgcolor: 'action.hover',
                                    '&:hover': { bgcolor: 'action.selected' }
                                }}
                                title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                            >
                                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                            </IconButton>
                            
                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{ 
                                    bgcolor: 'error.light',
                                    color: 'error.contrastText',
                                    '&:hover': { bgcolor: 'error.main' }
                                }}
                                title="Close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Toolbar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: { xs: 0.5, sm: 1, md: 1 },
                            p: { xs: 0.5, sm: 1, md: 1 },
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.default',
                            flexWrap: 'wrap',
                            flexShrink: 0,
                            minHeight: { xs: 48, sm: 56, md: 64 }
                        }}
                    >
                        {/* Navigation */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                            <IconButton
                                onClick={handlePreviousPage}
                                disabled={pages.length <= 1}
                                size="small"
                                sx={{ 
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    '&:hover': { bgcolor: 'primary.main' },
                                    '&:disabled': { bgcolor: 'action.disabledBackground' }
                                }}
                                title="Previous Page (←)"
                            >
                                <ChevronLeft />
                            </IconButton>
                            
                            <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
                                {currentPage + 1} / {pages.length}
                            </Typography>
                            
                            <IconButton
                                onClick={handleNextPage}
                                disabled={pages.length <= 1}
                                size="small"
                                sx={{ 
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    '&:hover': { bgcolor: 'primary.main' },
                                    '&:disabled': { bgcolor: 'action.disabledBackground' }
                                }}
                                title="Next Page (→)"
                            >
                                <ChevronRight />
                            </IconButton>
                        </Box>

                        {/* Zoom Controls */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                            <IconButton
                                onClick={handleZoomOut}
                                disabled={zoomLevel <= 0.5}
                                size="small"
                                title="Zoom Out (-)"
                            >
                                <ZoomOutIcon />
                            </IconButton>
                            
                            <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
                                {Math.round(zoomLevel * 100)}%
                            </Typography>
                            
                            <IconButton
                                onClick={handleZoomIn}
                                disabled={zoomLevel >= 3}
                                size="small"
                                title="Zoom In (+)"
                            >
                                <ZoomInIcon />
                            </IconButton>
                            
                            <Button
                                onClick={handleResetZoom}
                                size="small"
                                variant="outlined"
                                sx={{ minWidth: 60, fontSize: '0.75rem' }}
                                title="Reset View"
                            >
                                Reset
                            </Button>
                        </Box>

                        {/* Rotation Controls */}
                        <Box sx={{ 
                            display: { xs: 'none', sm: 'flex' }, 
                            alignItems: 'center', 
                            gap: 1 
                        }}>
                            <IconButton
                                onClick={handleRotateLeft}
                                size="small"
                                title="Rotate Left"
                            >
                                <RotateLeft />
                            </IconButton>
                            
                            <IconButton
                                onClick={handleRotateRight}
                                size="small"
                                title="Rotate Right"
                            >
                                <RotateRight />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Content Area */}
                    <Box
                        ref={containerRef}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            bgcolor: 'grey.100',
                            position: 'relative',
                            cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                            minHeight: { xs: '40vh', sm: '50vh', md: '60vh' }
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <Box
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: { xs: 1, sm: 1.5, md: 2 }
                            }}
                        >
                            {renderPageContent()}
                        </Box>

                        {/* Mobile Navigation Arrows */}
                        {isMobile && pages.length > 1 && (
                            <>
                                <IconButton
                                    onClick={handlePreviousPage}
                                    sx={{
                                        position: 'absolute',
                                        left: 16,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        color: 'white',
                                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                                        zIndex: 10
                                    }}
                                >
                                    <ChevronLeft />
                                </IconButton>
                                
                                <IconButton
                                    onClick={handleNextPage}
                                    sx={{
                                        position: 'absolute',
                                        right: 16,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        color: 'white',
                                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                                        zIndex: 10
                                    }}
                                >
                                    <ChevronRight />
                                </IconButton>
                            </>
                        )}
                    </Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            p: { xs: 1, sm: 1.5, md: 2 },
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: { xs: 1, sm: 2 },
                            flexShrink: 0,
                            minHeight: { xs: 48, sm: 56, md: 64 }
                        }}
                    >
                        <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                                display: { xs: 'none', sm: 'block' },
                                fontSize: { sm: '0.75rem', md: '0.875rem' }
                            }}
                        >
                            Use arrow keys or buttons to navigate • Scroll to zoom • Drag to pan when zoomed
                        </Typography>
                        <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                                display: { xs: 'block', sm: 'none' },
                                fontSize: '0.7rem'
                            }}
                        >
                            Tap arrows to navigate
                        </Typography>
                        
                        <Button
                            onClick={onClose}
                            variant="contained"
                            size="small"
                            sx={{ minWidth: 80 }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default ContractPreviewModal;